// @ts-nocheck
import LazyImage, { LazyImagePresentationProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import styled from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

export enum BadgeTypeValues {
    Image = "Image",
    Text = "Text",
}

export interface BadgePresentationProps {
    textBadgeCss?: InjectableCss;
    imageBadgeProps?: JSX.IntrinsicElements["img"];
}

/* Starter Custom Code - Start */
export const TextBadge = styled.div<{
    backgroundColor?: string;
    color?: string;
    badgeTextRounding?: number | boolean;
    padding?: string;
}>`
    display: flex;
    align-items: center;
    padding: ${props => props.padding || pxToRem(7.5, 12)};
    background-color: ${props => (props.backgroundColor ? `#${props.backgroundColor}` : getColor("common.accent"))};
    color: ${props => (props.color ? `#${props.color}` : getColor("common.accentContrast"))};
    border-radius: ${props =>
        typeof props.badgeTextRounding === "number"
            ? `${props.badgeTextRounding}px`
            : props.badgeTextRounding
              ? `4px`
              : `0px`};
    line-height: ${x => x.theme.design.typography.lineHeight.small};
`;
/* Starter Custom Code - End */

export type BadgeProps = {
    /** When type is Image an img tag will be rendered with the src being the value prop.
     * When the type is text a textbox will be rendered with the given value used as text and color scheme
     * denoted in badgeTextColor and badgeBackgroundColor
     * When no type is given or no corresponding value is given, nothing will render.
     */
    type: BadgeTypeValues;
    /** URL image path or badge text */
    value?: string;
    /** Alt text for the image badge */
    imageAltText?: string;
    /** Hex code for badge text color
     * Default is white (FFFFFF)
     */
    badgeTextColor?: string;
    /** Hex code for badge background color
     * Default is black (000000)
     */
    badgeBackgroundColor?: string;
    // Starter Custom Code
    /** Used to determine border radius for text badge
     */
    badgeTextRounding?: number | boolean;
    /** Used to determine padding for text badge
     */
    badgeTextPadding?: string;
} & BadgePresentationProps;

const Badge = ({
    type,
    value,
    imageAltText,
    badgeTextColor,
    badgeBackgroundColor,
    badgeTextRounding,
    imageBadgeProps,
    textBadgeCss,
    // Starter Custom Code
    badgeTextPadding,
}: BadgeProps) => {
    if (!type || !value) {
        return null;
    }

    if (type === BadgeTypeValues.Image) {
        return (
            <StyledWrapper title={imageAltText} /* Starter Custom Code */>
                <LazyImage imgProps={imageBadgeProps} src={value} altText={imageAltText} />
            </StyledWrapper>
        );
    }

    if (type === BadgeTypeValues.Text) {
        return (
            <TextBadge
                backgroundColor={badgeBackgroundColor} // Starter Custom Code
                color={badgeTextColor} // Starter Custom Code
                badgeTextRounding={badgeTextRounding} // Starter Custom Code
                data-has-background={!!badgeBackgroundColor} // Starter Custom Code
                padding={badgeTextPadding} // Starter Custom Code
            >
                <Typography {...textBadgeCss}>{value}</Typography>
            </TextBadge>
        );
    }

    return null; // Starter Custom Code
};

/** @component */
export default Badge;
